import React, { useMemo } from "react";
import { AHIcon } from "./AHIcon";
import { useScaleFactor } from "./AHScaleFactor";
import { Circle } from "./AHTheme/Circle";
import { CircleSize, useCircleSize } from "./LayoutProvider";
import { useTime } from "./useTime";
import { Duration, DateTime } from "luxon";

import { Box } from "./AHTheme/Box";
import { RemoteImage } from "./Image";
import { Image_image$key } from "./__generated__/Image_image.graphql";
import { TodoStatusAnimatedCircle } from "./TodoStatusAnimatedCircle";

const ONE_MINUTE = Duration.fromObject({ minutes: 1 }).as("milliseconds");
export function TodoStatusCircle(props: {
  size: CircleSize;
  status: "DONE" | "PENDING";
  expireAtISO: string | null;
  response?:
    | { __typename: "TodoResponseNumber" }
    | {
        __typename: "TodoResponseImage";
        images: readonly ({ id: string } & Image_image$key)[];
      }
    | { __typename: "TodoResponseText" }
    | { __typename: "%other" }
    | null;
}) {
  const now = useTime(ONE_MINUTE);
  const isExpired = useMemo(
    () => !!(props.expireAtISO && DateTime.fromISO(props.expireAtISO) < now),
    [props.expireAtISO, now]
  );

  const sf = useScaleFactor();
  const circleSize = useCircleSize(props.size);
  const smallCircle = useCircleSize("small");

  const shouldHideContents = circleSize < smallCircle;

  const tintColor =
    props.status === "DONE"
      ? "status-positive"
      : isExpired
        ? "status-negative"
        : "status-active";

  const fillColor = props.status === "DONE" ? "status-positive" : "transparent";

  if (
    props.status === "PENDING" &&
    props.response?.__typename === "TodoResponseImage"
  ) {
    return <AHIcon type="camera" color={tintColor} forceSize={circleSize} />;
  }

  if (
    !props.response &&
    process.env.EXPO_PUBLIC_DEVICE_PLATFORM_TV !== "true"
  ) {
    return (
      <TodoStatusAnimatedCircle
        size={props.size}
        status={props.status}
        isExpired={isExpired}
      />
    );
  }

  return (
    <Box>
      <Circle
        borderWidth={Math.max(1, Math.round(2 * sf))}
        size={props.size}
        borderColor={tintColor}
        color={fillColor}
        style={{ justifyContent: "center", alignItems: "center", padding: 2 }}
      >
        {shouldHideContents ? null : (
          <>
            {props.status === "DONE" ? (
              <AHIcon
                type="check"
                color="text-inverted"
                forceSize={circleSize * 0.5}
              />
            ) : props.response?.__typename === "TodoResponseText" ? (
              <AHIcon
                type="text-input"
                color={tintColor}
                forceSize={circleSize * 0.85}
              />
            ) : props.response?.__typename === "TodoResponseNumber" ? (
              <AHIcon
                type="number-input"
                color={tintColor}
                forceSize={circleSize * 0.85}
              />
            ) : null}
          </>
        )}
      </Circle>
      {props.status === "DONE" && props.response ? (
        <Box
          style={{
            position: "absolute",
            top: -smallCircle / 3,
            left: -smallCircle / 3,
          }}
        >
          <Circle
            size="small"
            color="positive"
            style={{
              shadowColor: "#000",
              shadowOffset: {
                width: 0,
                height: 0,
              },
              shadowOpacity: 0.25,
              shadowRadius: 2,
              elevation: 1,
            }}
          >
            {props.response?.__typename === "TodoResponseNumber" ? (
              <AHIcon
                type="number-input"
                color="text-inverted"
                forceSize={smallCircle}
              />
            ) : props.response?.__typename === "TodoResponseText" ? (
              <AHIcon
                type="text-input"
                color="text-inverted"
                forceSize={smallCircle}
              />
            ) : props.response?.__typename === "TodoResponseImage" &&
              props.response?.images[0] ? (
              <RemoteImage
                image={props.response?.images[0]}
                style={{
                  borderRadius: smallCircle / 2,
                  height: smallCircle,
                  width: smallCircle,
                }}
              />
            ) : null}
          </Circle>
        </Box>
      ) : null}
    </Box>
  );
}
